import {useState, useEffect} from "react";
import {springConfigs, useReveal, Portal} from "@cdx/common";
import {animated} from "react-spring";
import {Col, css, DSButton, Row, Text} from "@cdx/ds";
import {colorThemes, subThemes} from "@cdx/ds/css/themes/themes.css";
import {api} from "../lib/api";
import routes from "../routes";
import cdxEnv from "../env";
import {API_ERRORS, apiErrorEvent} from "../lib/request-utils";

// ── CLIENT ERROR CODE TABLE ──────────────────────────────────────────────────────────────────
// The toast never surfaces a raw Error.message, stack, or server payload — those read as debug
// dumps and aren't actionable. Instead every error resolves to a short CATEGORY plus a numeric
// CODE, rendered together as "CATEGORY (CODE)".
//
//   • The CATEGORY (a short human phrase in a consistent writing style) tells the user the broad
//     TYPE of failure — network vs server vs their own request — without overwhelming them.
//   • The CODE is the precise internal pointer to the actual failing component / HTTP status /
//     server-sent cause, so a reported issue stays diagnosable even though the message is short.
//     Multiple codes share one category (e.g. every 5xx is "Server error", with the code naming
//     the exact status).
//
// This EXTENDS the server-side error scheme Daniel added on main rather than inventing a parallel
// one (node-api/src/lib/http-error.ts — HttpError.code, a stable snake_case `error` field in the
// 400 body; node-api/src/lib/graph-api/errors.ts — GraphQueryError's code set). Where the server
// sends one of those snake_case codes in a 400 body, we translate it into the matching numeric
// code below (see codeForServerCode); otherwise we fall back to the HTTP status / transport cause.
//
//   Category              Range    Code  Points at
//   ─────────────────────────────────────────────────────────────────────────────────────────
//   Server unreachable    600–609  601   api transport failure (couldn't reach the api at all)
//                                  602   (reserved) image server / uploads transport failure
//                                  603   (reserved) realtime / push (websocket) transport failure
//   Request timed out     610–619  611   api request timed out / aborted (not user-initiated)
//   Server error          620–629  621   500 internal   622 502   623 503   624 504   629 other 5xx
//   Request rejected      630–649  631   400 invalid_query_shape (query wasn't a valid object)
//                                  632   400 malformed graph query (unknown_model/field/relation/
//                                        operator, invalid_value, invalid_order/limit, …)
//                                  633   400 other bad request (display_error / generic 400)
//                                  634   403 missing_scope / not authorised (403 normally redirects)
//                                  635   404 not found     636 409 conflict     637 422 validation
//                                  638   429 rate limited  639 other 4xx
//   Something went wrong  690–699  691   unclassified client / app error (no HTTP status)
// ──────────────────────────────────────────────────────────────────────────────────────────────
const ERROR_CATEGORIES = {
  unreachable: "Server unreachable",
  timeout: "Request timed out",
  serverError: "Server error",
  rejected: "Request rejected",
  unknown: "Something went wrong",
};

// GraphQueryError's malformed-query codes (node-api/src/lib/graph-api/errors.ts) — all collapse to
// one numeric code (632) since they're the same category of "your graph query was malformed".
const GRAPH_QUERY_CODES = new Set([
  "unknown_model",
  "unknown_field",
  "unknown_relation",
  "unknown_operator",
  "invalid_value",
  "unknown_special_key",
  "invalid_order",
  "invalid_limit",
  "missing_ids",
  "relation_takes_no_query",
  "invalid_aggregate",
]);

// A 400 body carries the server's stable snake_case `error` code (Daniel's HttpError scheme).
const getServerCode = (message) =>
  message && typeof message === "object" && typeof message.error === "string"
    ? message.error
    : null;

// server snake_case code -> our numeric code (within the "Request rejected" range).
const codeForServerCode = (serverCode) => {
  if (serverCode === "invalid_query_shape") return 631;
  if (GRAPH_QUERY_CODES.has(serverCode)) return 632;
  if (serverCode === "display_error") return 633;
  if (serverCode === "missing_scope") return 634;
  return null;
};

// Fallback when no recognised server code is present: derive the numeric code from the 4xx status.
const codeForClientStatus = (status) => {
  switch (status) {
    case 400:
      return 633;
    case 403:
      return 634;
    case 404:
      return 635;
    case 409:
      return 636;
    case 422:
      return 637;
    case 429:
      return 638;
    default:
      return 639;
  }
};

const isTimeout = (err) => {
  const name = err && (err.name || (err.error && err.error.name));
  return name === "AbortError" || name === "TimeoutError";
};

// Resolves an HTTP error to its {category, code}. 5xx -> "Server error" (code = the exact status);
// 4xx -> "Request rejected" (code = the server's snake_case code translated, else the status).
const resolveHttpError = (status, message) => {
  if (status >= 500) {
    const serverErrorCodes = {500: 621, 502: 622, 503: 623, 504: 624};
    return {category: ERROR_CATEGORIES.serverError, code: serverErrorCodes[status] || 629};
  }
  if (status >= 400) {
    const serverCode = getServerCode(message);
    const code = (serverCode && codeForServerCode(serverCode)) || codeForClientStatus(status);
    return {category: ERROR_CATEGORIES.rejected, code};
  }
  return {category: ERROR_CATEGORIES.unknown, code: 691};
};

export const ApiErrors = ({history, location}) => {
  const [error, setError] = useState(null);
  const [redirectSubdomain, setRedirectSubdomain] = useState(null);

  const handleRetry = () =>
    new Promise((resolve) =>
      api.cache.clear({
        onDone: () => {
          setError(null);
          resolve();
        },
      })
    );

  useEffect(
    () =>
      apiErrorEvent.addListener((e) => {
        if (e === null) {
          setError(null);
          return;
        }
        switch (e.type) {
          case API_ERRORS.FETCH_ERROR: {
            const timedOut = isTimeout(e);
            setError({
              type: "retryable",
              category: timedOut ? ERROR_CATEGORIES.timeout : ERROR_CATEGORIES.unreachable,
              code: timedOut ? 611 : 601,
              link:
                cdxEnv.ON_PREMISE !== "true"
                  ? {url: "https://status.codecks.io", label: "Open Status Page"}
                  : undefined,
            });
            return;
          }
          case API_ERRORS.API_VERSION_MISMATCH: {
            setError({
              type: "versionMismatch",
              message: "Codecks has been updated. Please refresh this window.",
            });
            return;
          }
          case API_ERRORS.API_ERROR: {
            switch (e.status) {
              case 401: {
                api.cache.clear({
                  delayReload: true,
                  onDone: () => {
                    history.replace({
                      pathname: "/login",
                      state: {from: location},
                    });
                  },
                });
                return;
              }
              case 403:
                api.cache.clear({
                  delayReload: true,
                  onDone: () => {
                    if (e.message === "too_many_projects" || e.message === "too_many_seats") {
                      history.push(routes.freeLimitsExceeded.getUrl());
                    } else {
                      history.push(routes.notAuthorised.getUrl());
                    }
                  },
                });
                return;
              default: {
                // No raw payload reaches the toast: resolveHttpError maps the status (and, for a
                // 400, the server's snake_case `error` code) to a short CATEGORY + numeric CODE.
                const {category, code} = resolveHttpError(e.status, e.message);
                setError((prev) => {
                  if (prev && prev.category === category && prev.code === code) return prev;
                  return {type: "default", category, code};
                });
                return;
              }
            }
          }
          case API_ERRORS.NEW_SUBDOMAIN: {
            setRedirectSubdomain(e.value);
            return;
          }
          default: {
            // Any error we didn't classify above: a short, generic indicator — never a raw dump.
            const timedOut = isTimeout(e);
            setError({
              type: "default",
              category: timedOut ? ERROR_CATEGORIES.timeout : ERROR_CATEGORIES.unknown,
              code: timedOut ? 611 : 691,
            });
          }
        }
      }),
    [history, location]
  );

  const reveal = useReveal(
    error ||
      (redirectSubdomain
        ? {
            type: "newSubdomain",
            message: "The URL of your Organization has changed",
            value: redirectSubdomain,
          }
        : null),
    {config: springConfigs.quick}
  );

  return reveal((props, renderError) => {
    const isWarning = renderError.type === "versionMismatch" || renderError.type === "newSubdomain";
    const getBottomParts = () => {
      const parts = [];
      if (renderError.type === "newSubdomain") {
        parts.push(
          <DSButton
            onClick={handleRetry}
            href={cdxEnv.HOST_PATTERN.replace("[SUB]", renderError.value)}
          >
            Open{" "}
            {cdxEnv.HOST_PATTERN.replace("[SUB]", renderError.value).replace(/https?:\/\//, "")}
          </DSButton>
        );
      }
      if (renderError.type === "retryable") {
        parts.push(<DSButton onClick={handleRetry}>Retry</DSButton>);
      }
      if (renderError.link) {
        parts.push(
          <DSButton
            variant="secondary"
            onClick={handleRetry}
            href={renderError.link.url}
            target="_blank"
            rel="noopener"
          >
            {renderError.link.label}
          </DSButton>
        );
      }
      if (renderError.type === "versionMismatch") {
        parts.push(<DSButton onClick={() => window.location.reload()}>Reload</DSButton>);
      }
      return parts;
    };

    const bottomParts = getBottomParts();

    return (
      <Portal>
        <animated.div
          style={{zIndex: 50, opacity: props.value}}
          className={css({
            display: "flex",
            flexDir: "column",
            align: "center",
            position: "fixed",
            left: 0,
            right: 0,
            bottom: 0,
            pointerEvents: "none",
            pb: "8px",
            px: "8px",
          })}
        >
          <Col
            className={isWarning ? colorThemes.purple50 : subThemes.alert}
            bg
            elevation={200}
            px="16px"
            py="12px"
            rounded="4"
            pointerEvents="auto"
            sp="12px"
            align="center"
            // Compact toast: shrink-wraps to the (now short) message, capped to a readable measure
            // on wide screens and never wider than the viewport on mobile (~390px).
            style={{maxWidth: "min(480px, 100%)"}}
          >
            <Text type="label14" color="primary" allowWrap overflowWrap="break-word">
              {renderError.category ? (
                // "CATEGORY (CODE)" — the human category, then its numeric diagnostic code as a
                // compact muted suffix on the same line.
                <>
                  {renderError.category}{" "}
                  <Text as="span" type="label14" color="secondary" fontFamily="mono" allowWrap>
                    ({renderError.code})
                  </Text>
                </>
              ) : (
                // Warnings (version mismatch / new subdomain) carry a plain message, no code.
                renderError.message
              )}
            </Text>
            {bottomParts.length > 0 && (
              <Row justify="center" sp="8px">
                {bottomParts}
              </Row>
            )}
          </Col>
        </animated.div>
      </Portal>
    );
  });
};
